/* PrimeCut cart safety net for Kaching Bundles' free gifts, plus free-shipping progress for non-USD carts.

   Kaching Bundles is the only system that ADDS and DISCOUNTS gifts. It owns the offer: the preset-bundle deal adds a
   free e-book and a free $10 gift card and brings both to $0, and the single-tee deals add their own gifts. Each gift
   set belongs to the bundle line it came with, so every qualifying bundle carries its own — that is deliberate, and
   nothing here trims it. This script never adds a gift, never prices one, and never touches a line Kaching did not
   mark as a gift.

   It corrects exactly one state Kaching leaves behind: an orphaned gift. Once a gift line is no longer free — for
   example after the qualifying bundle is removed, which otherwise leaves the e-book in the cart at its full $39.99 —
   the line is removed rather than silently charged. A line only counts as a gift when Kaching itself marked it as one
   (property __kaching_bundles with a "gift" key), so an e-book or gift card the customer genuinely chose to buy is
   never touched.

   It also keeps the preset-bundle gift rules true as the cart changes, touching only Kaching-marked gift lines:
   - one $10 gift card per preset-bundle unit: when a bundle's quantity changes, its own gift-card line follows it
     (a card the customer removed is not put back);
   - one promotional e-book per cart, whatever the number of bundles or Kaching deals: extra promotional copies are
     dropped, and when the bundle holding the e-book is removed while another preset bundle stays, the e-book moves to
     that bundle (unless the customer removed the e-book themselves).
   A preset bundle is a Kaching "main" line whose SKU follows the pack rule PC-PK{qty}-…; single tees and Kaching's
   mixed-tee deals never count as one, so their gift cards are left exactly as Kaching set them.

   Everything runs behind a busy flag with a capped number of passes, so it can never fight Kaching in a loop. */
(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  const style = document.createElement('style');
  style.textContent = `
    .pc-ship-progress { margin: 10px 16px 6px; text-align: center; }
    #main-cart-footer .pc-ship-progress { margin: 0 0 16px; text-align: left; }
    .pc-ship-progress__text { margin: 0 0 8px !important; color: #111 !important; font-size: 14px; font-weight: 500; line-height: 1.35; }
    .pc-ship-progress__bar { height: 6px; overflow: hidden; border-radius: 999px; background: #e6e6e6; }
    .pc-ship-progress__bar span { display: block; height: 100%; border-radius: inherit; background: #111; transition: width 0.3s ease; }
    @media (prefers-reduced-motion: reduce) { .pc-ship-progress__bar span { transition: none; } }`;
  document.head.appendChild(style);

  // Kaching's line data. cart.js exposes __kaching_bundles even though Liquid cannot read it.
  const kaching = (item) => {
    const raw = item && item.properties && item.properties.__kaching_bundles;
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data && typeof data === 'object' ? data : null;
    } catch (e) {
      return null;
    }
  };
  // A line Kaching added as a gift.
  const isGift = (item) => {
    const data = kaching(item);
    return !!(data && data.gift);
  };
  const GIFT_CARD_HANDLE = 'primecut-gift-card';
  const EBOOK_HANDLE = 'stand-tall';
  const PACK_SKU = /^PC-PK\d+-/;
  const isCardGift = (item) => isGift(item) && item.handle === GIFT_CARD_HANDLE;
  const isEbookGift = (item) => isGift(item) && item.handle === EBOOK_HANDLE;

  // Kaching groups (shared "id") whose main line is a preset bundle, in cart order, with their total bundle units.
  const presetGroups = (cart) => {
    const groups = new Map();
    cart.items.forEach((item) => {
      const data = kaching(item);
      if (!data || !data.main || !data.id || !PACK_SKU.test(item.sku || '')) return;
      const group = groups.get(data.id) || { id: data.id, deal: data.deal, v: data.v, units: 0 };
      group.units += item.quantity;
      groups.set(data.id, group);
    });
    return [...groups.values()];
  };

  /* The promotional e-book is remembered per cart (by token) so a missing one can be told apart: moved away with its
     removed bundle (re-home it) or removed by the customer (leave it out). */
  const EBOOK_KEY = 'pc-promo-ebook';
  const readEbookMemory = (token) => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(EBOOK_KEY) || 'null');
      return saved && saved.token === token ? saved : null;
    } catch (e) {
      return null;
    }
  };
  const writeEbookMemory = (memory) => {
    try {
      sessionStorage.setItem(EBOOK_KEY, JSON.stringify(memory));
    } catch (e) {
      /* private mode: the e-book is simply not re-homed */
    }
  };

  let cartState = null;
  let busy = false;
  let rerun = false;

  const getCart = async () =>
    (await nativeFetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })).json();

  const setQuantity = async (key, quantity) =>
    nativeFetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity }),
    });

  const addLine = async (item) =>
    nativeFetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [item] }),
    });

  /* The single correction this cart needs, or null when the gift lines are already right.
     One change per pass: Shopify re-keys lines whenever a discount allocation moves, so the cart is re-read between
     passes rather than trusting stale keys. */
  const nextFix = (cart) => {
    // A gift that is no longer free is not a gift. Drop it rather than leave the customer paying for it.
    const charged = cart.items.filter(isGift).find((item) => item.final_line_price > 0);
    if (charged) return { key: charged.key, quantity: 0, why: 'orphaned' };

    // One promotional e-book per cart: keep the first, drop the rest.
    const ebooks = cart.items.filter(isEbookGift);
    if (ebooks.length > 1) return { key: ebooks[ebooks.length - 1].key, quantity: 0, why: 'extra-ebook' };
    if (ebooks.length === 1 && ebooks[0].quantity > 1) return { key: ebooks[0].key, quantity: 1, why: 'extra-ebook' };

    // One $10 gift card per preset-bundle unit, on the bundle's own group.
    const groups = presetGroups(cart);
    for (const group of groups) {
      const cards = cart.items.filter((item) => isCardGift(item) && kaching(item).id === group.id);
      if (!cards.length) continue; // none added, or the customer removed it
      if (cards.length > 1) return { key: cards[cards.length - 1].key, quantity: 0, why: 'card-sync' };
      if (cards[0].quantity !== group.units) return { key: cards[0].key, quantity: group.units, why: 'card-sync' };
    }

    // Re-home the e-book when its bundle left the cart but another preset bundle stayed.
    if (!ebooks.length && groups.length) {
      const memory = readEbookMemory(cart.token);
      if (memory && !memory.declined && memory.gift && !groups.some((g) => g.id === memory.group)) {
        const target = groups[0];
        const data = { id: target.id, deal: target.deal, gift: memory.gift };
        if (target.v) data.v = target.v;
        return { add: { id: memory.variant, quantity: 1, properties: { __kaching_bundles: JSON.stringify(data) } }, why: 'ebook-rehome' };
      }
    }
    return null;
  };

  // Remember where the promotional e-book sits, and whether the customer removed it while its bundle stayed.
  const noteEbook = (cart) => {
    const ebook = cart.items.find(isEbookGift);
    const memory = readEbookMemory(cart.token);
    if (ebook) {
      const data = kaching(ebook);
      writeEbookMemory({ token: cart.token, group: data.id, gift: data.gift, variant: ebook.variant_id, declined: false });
    } else if (memory && !memory.declined && presetGroups(cart).some((g) => g.id === memory.group)) {
      writeEbookMemory({ ...memory, declined: true });
    }
  };

  async function refreshViews() {
    if (document.querySelector('cart-items') && /\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    try {
      const response = await nativeFetch(`${ROOT}?sections=cart-drawer,cart-icon-bubble`, { credentials: 'same-origin' });
      const sections = await response.json();
      const inner = (html, selector) => {
        const el = html && new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return el ? el.innerHTML : null;
      };
      const drawerHtml = inner(sections['cart-drawer'], '#CartDrawer');
      const target = document.getElementById('CartDrawer');
      if (target && drawerHtml !== null) target.innerHTML = drawerHtml;
      const bubbleHtml = inner(sections['cart-icon-bubble'], '.shopify-section');
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && bubbleHtml !== null) bubble.innerHTML = bubbleHtml;
      drawer.classList.toggle('is-empty', !!cartState && cartState.item_count === 0);
    } catch (e) {
      /* the next cart update will refresh the drawer */
    }
  }

  async function reconcile() {
    if (busy) {
      rerun = true;
      return;
    }
    busy = true;
    try {
      let cart = await getCart();
      let changed = false;
      for (let pass = 0; pass < 6; pass += 1) {
        const fix = nextFix(cart);
        if (!fix) break;
        if (fix.add) {
          const response = await addLine(fix.add);
          if (!response.ok) break;
          cart = await getCart();
        } else {
          const response = await setQuantity(fix.key, fix.quantity);
          if (!response.ok) break;
          cart = await response.json();
        }
        changed = true;
      }
      noteEbook(cart);
      cartState = cart;
      if (changed) await refreshViews();
      render();
    } catch (e) {
      /* offline or cart unavailable: leave the cart untouched */
    } finally {
      busy = false;
      if (rerun) {
        rerun = false;
        reconcile();
      }
    }
  }

  // Free-shipping progress for non-USD carts: checkout compares the cart subtotal, converted to USD, with US$65.
  const SHIP_THRESHOLD_USD_CENTS = 6500;
  function renderShipProgress() {
    document.querySelectorAll('[data-pc-ship-progress]').forEach((box) => {
      const code = box.dataset.currency || 'USD';
      if (code === 'USD') return;
      const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '0');
      const text = box.querySelector('.pc-ship-progress__text');
      const bar = box.querySelector('.pc-ship-progress__bar span');
      if (!rate || !text) return;
      const subtotal = parseInt(box.dataset.subtotal, 10) || 0;
      const thresholdLocal = Math.ceil(SHIP_THRESHOLD_USD_CENTS * rate);
      const remaining = Math.max(0, thresholdLocal - subtotal);
      let label;
      try {
        label = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(remaining / 100);
      } catch (e) {
        label = `${(remaining / 100).toFixed(2)} ${code}`;
      }
      const message = remaining > 0 ? `You're ${label} away from free shipping (US$65).` : "You've unlocked free shipping!";
      if (text.textContent !== message) text.textContent = message;
      if (bar) bar.style.width = `${Math.min(100, Math.floor((subtotal / thresholdLocal) * 100))}%`;
    });
  }

  // After Audit: bundle grouping in the cart drawer and cart page. Purely visual: lines that belong to the same
  // bundle (the bundle app's own group id on each line) get a shared frame, a custom pack gets one heading with its
  // pack price, and each gift is shown under the bundle it came with. No cart line, quantity or property is changed,
  // separate bundles are never merged, and nothing is written to the page when it is already up to date.
  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };
  function renderGroups() {
    const cart = cartState;
    if (!cart || !Array.isArray(cart.items)) return;
    const byKey = new Map(cart.items.map((item) => [item.key, item]));
    const bodies = new Set();
    document.querySelectorAll('tr[data-pc-line-key]').forEach((row) => bodies.add(row.parentElement));
    bodies.forEach((body) => {
      const rows = [...body.querySelectorAll('tr[data-pc-line-key]')];
      const info = rows.map((row) => {
        const item = byKey.get(row.dataset.pcLineKey);
        const data = item ? kaching(item) : null;
        return { row, item, group: data && data.id ? String(data.id) : '', gift: !!(data && data.gift), main: !!(data && data.main) };
      });
      // The drawer was re-rendered before the cart was re-read: wait for the next pass instead of guessing.
      if (info.some((entry) => !entry.item)) return;
      const heads = new Map();
      info.forEach((entry, index) => {
        const { row, group } = entry;
        const previous = index > 0 ? info[index - 1].group : '';
        const next = index < info.length - 1 ? info[index + 1].group : '';
        const flags = {
          'pc-line--grouped': !!group,
          'pc-line--first': !!group && previous !== group,
          'pc-line--last': !!group && next !== group,
          'pc-line--sub': !!group && entry.gift,
        };
        Object.entries(flags).forEach(([name, on]) => {
          if (row.classList.contains(name) !== on) row.classList.toggle(name, on);
        });
        if (!group) return;
        const members = info.filter((other) => other.group === group);
        const mains = members.filter((other) => other.main);
        const custom = mains.length > 0 && mains.every((other) => !PACK_SKU.test(other.item.sku || ''));
        if (row.classList.contains('pc-line--custom') !== custom) row.classList.toggle('pc-line--custom', custom);
        if (custom && flags['pc-line--first']) {
          const pack = (mains[0].item.properties && mains[0].item.properties.Pack) || 'Custom pack';
          const total = mains.reduce((sum, other) => sum + other.item.final_line_price, 0);
          heads.set(row, { title: pack, price: money(total, cart.currency) });
        }
      });
      // One heading row above each custom pack (never inside a .cart-item row, so quantity and remove controls keep
      // their own row ids).
      [...body.querySelectorAll('tr.pc-line-head')].forEach((head) => {
        const owner = head.nextElementSibling;
        const want = owner && heads.get(owner);
        if (!want || head.dataset.title !== want.title || head.dataset.price !== want.price) head.remove();
        else heads.delete(owner);
      });
      heads.forEach((want, row) => {
        const head = document.createElement('tr');
        head.className = 'pc-line-head';
        head.dataset.title = want.title;
        head.dataset.price = want.price;
        const cell = document.createElement('td');
        cell.colSpan = 6;
        const title = document.createElement('span');
        title.className = 'pc-line-head__title';
        title.textContent = want.title;
        const price = document.createElement('span');
        price.className = 'pc-line-head__price';
        price.textContent = `Pack price ${want.price}`;
        cell.append(title, price);
        head.appendChild(cell);
        body.insertBefore(head, row);
      });
    });
  }

  let renderQueued = false;
  function render() {
    renderShipProgress();
    renderGroups();
  }
  const renderSoon = () => {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      render();
    });
  };

  let timer = 0;
  const schedule = (delay = 350) => {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay);
  };

  window.fetch = function pcCartGiftFetch(input, init) {
    const request = nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input && input.url) || '';
      if (CART_MUTATION.test(url)) request.then(() => schedule(), () => {});
    } catch (e) {
      /* never interfere with the request */
    }
    return request;
  };

  const start = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(renderSoon).observe(drawer, { childList: true, subtree: true });
    const cartPage = document.getElementById('main-cart-footer');
    if (cartPage) new MutationObserver(renderSoon).observe(cartPage, { childList: true, subtree: true });
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => schedule());
    }
    reconcile();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
